import React from 'react';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import DataSourceProvider from '../DataSource/DataSourceProvider';
import DataGridColumnSelection from './DataGridColumnSelection';
import DataGridToolbar from './DataGridToolbar';
import DataGridSelectionToolbar from './DataGridSelectionToolbar';
import DataGridHeader from './DataGridHeader';
import DataGridFooter from './DataGridFooter';
import DataGridRows from './DataGridRows';
import DataGridSaveView from './DataGridSaveView';
import DataGridLoader from './DataGridLoader';
import DataGridSortSelection from './DataGridSortSelection';
import DataGridExportSelection from './DataGridExportSelection';
import DataGridAddColumn from './DataGridAddColumn';
import DataGridViews from './DataGridViews';
import FilterSelection from '../DataSource/FilterSelection';
import ViewSelection from '../DataSource/ViewSelection';
import styled from 'styled-components';

const DataGridContainer = styled.div`
  width: 100%;
`;

const ToolbarContainer = styled.div`
  min-height: ${props => (props.showSelectionToolbar ? '64px' : 'auto')};
  position: relative;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const DataGrid = ({
  dataSource,
  size,
  bulkActions,
  actions,
  formatters,
  filterFormatters,
  filterRenderers,
}) => {
  const {
    data,
    isLoadingData,
    hasModule,
    columnSelection,
    filterSelection,
    viewsSelection,
    advancedSort,
    dataExport,
    selection = {},
    pagination = {},
  } = dataSource;
  const { itemCount = data.length } = pagination;
  const { selectedItems = [], deselectedItems = [], allItemsSelected = false } = selection;
  const hasSelectionModule = hasModule('selection');
  const hasSelectedItems = allItemsSelected
    ? deselectedItems.length < itemCount
    : selectedItems.length > 0;
  const showSelectionToolbar = hasSelectionModule && hasSelectedItems;
  const hasPaginationModule = hasModule('pagination');
  const hasViewsModule = hasModule('views');

  return (
    <DataSourceProvider value={dataSource}>
      <DataGridContainer>
        <ToolbarContainer showSelectionToolbar={showSelectionToolbar}>
          {hasViewsModule && viewsSelection && <DataGridViews />}
          {showSelectionToolbar && <DataGridSelectionToolbar bulkActions={bulkActions} />}
          <DataGridToolbar actions={actions} filterRenderers={filterRenderers} />
        </ToolbarContainer>
        <TableContainer>
          <Table size={size}>
            <DataGridHeader />
            {isLoadingData ? (
              <DataGridLoader size={size} />
            ) : (
              <DataGridRows formatters={formatters} />
            )}
          </Table>
        </TableContainer>
        {hasPaginationModule && <DataGridFooter />}
      </DataGridContainer>

      {columnSelection && (
        <>
          <DataGridColumnSelection />
          <DataGridAddColumn />
        </>
      )}

      {filterSelection && (
        <FilterSelection filterFormatters={filterFormatters} filterRenderers={filterRenderers} />
      )}
      {advancedSort && <DataGridSortSelection />}
      {dataExport && <DataGridExportSelection />}

      {viewsSelection && (
        <>
          <ViewSelection />
          <DataGridSaveView filterRenderers={filterRenderers} />
        </>
      )}
    </DataSourceProvider>
  );
};

DataGrid.propTypes = {
  actions: PropTypes.array,
  bulkActions: PropTypes.array,
  dataSource: PropTypes.object,
  filterFormatters: PropTypes.object,
  filterRenderers: PropTypes.object,
  formatters: PropTypes.object,
  size: PropTypes.string,
};

DataGrid.defaultProps = {
  actions: [],
  bulkActions: [],
  dataSource: {},
  filterFormatters: {},
  filterRenderers: {},
  formatters: {},
  size: 'medium',
};

export default DataGrid;
